import React, { useEffect, useMemo } from "react";
import styles from "./BillingResultList.module.scss";
import { Backdrop, CircularProgress, Typography } from "@material-ui/core";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getBillingResults } from "nodeDetails/nodeDetails.actions";
import { IApplicationState } from "redux/root-reducer";
import Item from "./Item";
import { IBillingResult } from "nodeDetails/types";
import { UnixToDateConverter } from "common/DateConverter";
import { useTranslation } from "react-i18next";

const BillingResultList = () => {
  const { domain, id: nodeId } = useParams();

  const dispatch = useDispatch();
  const {
    billingResults,
    isFetchingBillingResults: isFetching,
    fetchBillingResultsFailed,
  } = useSelector((state: IApplicationState) => state.node);

  useEffect(() => {
    dispatch(getBillingResults(domain, nodeId));
  }, []);
  const { t } = useTranslation();

  const billingResultsByYear = useMemo(() => {
    const byYearObject: Record<string, IBillingResult[]> =
      billingResults.reduce(
        (acc: Record<string, IBillingResult[]>, billingResult) => {
          const year = UnixToDateConverter(billingResult.Start).year();

          return {
            ...acc,
            [year]: [...(acc[year] || []), billingResult],
          };
        },
        {}
      );

    const byYearArray = Object.entries(byYearObject);
    // Sort year
    byYearArray.sort(([a], [b]) => parseInt(b) - parseInt(a));

    // Sort billing results under year
    byYearArray.forEach((item) => {
      item[1].sort((a, b) => b.Start - a.Start);
    });

    return byYearArray;
  }, [billingResults]);

  if (isFetching && billingResults.length === 0) {
    return (
      <Backdrop open={isFetching}>
        <CircularProgress color="secondary" />
      </Backdrop>
    );
  }

  if (fetchBillingResultsFailed) return null;

  return (
    <div className={styles.container}>
      <Typography gutterBottom variant="h1">
        {t("Invoice data")}
      </Typography>
      {billingResultsByYear.map(([year, billingResults]) => (
        <div className={styles.section} key={year}>
          <Typography gutterBottom variant="h2">
            {year}
          </Typography>
          {billingResults.map((billingResult) => (
            <Item key={billingResult.ID} billingResult={billingResult} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default BillingResultList;
